import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaCalendarAlt,
  FaTimes,
  FaCertificate,
  FaAward,
} from "react-icons/fa";
import NewlyAppliedClients from "../../components/auth/Our Clients/NewlyAppliedClients";
import ApprovedClients from "../../components/auth/Our Clients/ApprovedClients";
import SuspendedClients from "../../components/auth/Our Clients/SuspendedClients";
import { Pagination, Stack } from "@mui/material";
import AddClientModal from "../../components/Modals/AddClientModal";
import ActiveClients from "../../components/auth/Our Clients/ActiveClients";
import WithdrawalClients from "../../components/auth/Our Clients/WithdrawalClients";
import { GrDownload } from "react-icons/gr";
import exportToExcel from "../../utils/exportToExcel";
import axios from "axios";
import * as XLSX from "xlsx";
import { LiaCertificateSolid } from "react-icons/lia";
import CertificateExpiring from "../../components/auth/Our Clients/CertificateExpiring";
import AnnualSurveillanceDueClient from "../../components/auth/Our Clients/AnnualSurveillanceDueClient";

const OurClients = () => {
  const [activeTab, setActiveTab] = useState("Active");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [filterType, setFilterType] = useState("single");
  const [singleMonth, setSingleMonth] = useState("");
  const [singleYear, setSingleYear] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [startYear, setStartYear] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [endYear, setEndYear] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [filteredData, setFilteredData] = useState([]);

  // Certificate filter states - for ISO 9001:2015
  const [showCertificateFilter, setShowCertificateFilter] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState("");
  const [appliedCertificateFilter, setAppliedCertificateFilter] =
    useState(null);
  const [certificateFilteredData, setCertificateFilteredData] = useState([]);

  // Certificate configuration - use "ISO 9001:2015" as identifier
  const ISO_CERTIFICATE = {
    _id: "ISO_9001_2015", // Use certificate name as identifier
    certificateName: "ISO 9001:2015",
  };

  const itemsPerPage = 8;

  const months = [
    { value: "01", label: "Jan" },
    { value: "02", label: "Feb" },
    { value: "03", label: "Mar" },
    { value: "04", label: "Apr" },
    { value: "05", label: "May" },
    { value: "06", label: "Jun" },
    { value: "07", label: "Jul" },
    { value: "08", label: "Aug" },
    { value: "09", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setQuery("");
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDataChange = (pages, items, data) => {
    setTotalPages(pages);
    setTotalItems(items);
    if (data) setFilteredData(data);
  };

  const handleCertificateDataChange = (data) => {
    setCertificateFilteredData(data);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditModalOpen(false);
    setSelectedClient(null);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditModalOpen(true);
  };

  const applyDateFilter = () => {
    if (filterType === "single" && singleMonth && singleYear) {
      const monthName = months.find((m) => m.value === singleMonth)?.label;
      setAppliedFilter(`${monthName} ${singleYear}`);
    } else if (
      filterType === "range" &&
      startMonth &&
      startYear &&
      endMonth &&
      endYear
    ) {
      const startMonthName = months.find((m) => m.value === startMonth)?.label;
      const endMonthName = months.find((m) => m.value === endMonth)?.label;
      setAppliedFilter(
        `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`
      );
    }
    setShowDateFilter(false);
    setPage(1);
  };

  const clearDateFilter = () => {
    setAppliedFilter(null);
    setSingleMonth("");
    setSingleYear("");
    setStartMonth("");
    setStartYear("");
    setEndMonth("");
    setEndYear("");
    setPage(1);
  };

  const resetFilterForm = () => {
    setSingleMonth("");
    setSingleYear("");
    setStartMonth("");
    setStartYear("");
    setEndMonth("");
    setEndYear("");
  };

  // Certificate filter functions
  const applyCertificateFilter = () => {
    if (selectedCertificate) {
      setAppliedCertificateFilter("ISO 9001:2015");
    }
    setShowCertificateFilter(false);
    setPage(1);
  };

  const clearCertificateFilter = () => {
    setAppliedCertificateFilter(null);
    setSelectedCertificate("");
    setPage(1);
  };

  const getFilterData = () => {
    if (filterType === "single" && singleMonth && singleYear) {
      return {
        type: "single",
        month: singleMonth,
        year: singleYear,
      };
    } else if (
      filterType === "range" &&
      startMonth &&
      startYear &&
      endMonth &&
      endYear
    ) {
      return {
        type: "range",
        startMonth,
        startYear,
        endMonth,
        endYear,
      };
    }
    return null;
  };

  const getCertificateFilterData = () => {
    return selectedCertificate
      ? {
          certificateName: "ISO 9001:2015", // Pass certificate name for filtering
        }
      : null;
  };

  const commonProps = {
    query,
    page,
    itemsPerPage,
    onDataChange: handleDataChange,
    onCertificateDataChange: handleCertificateDataChange,
    onEdit: handleEdit,
    dateFilter: getFilterData(),
    certificateFilter: getCertificateFilterData(),
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Newly Applied":
        return <NewlyAppliedClients {...commonProps} />;
      case "Approved":
        return <ApprovedClients {...commonProps} />;
      case "Suspended":
        return <SuspendedClients {...commonProps} />;
      case "Active":
        return <ActiveClients {...commonProps} />;
      case "Withdrawal":
        return <WithdrawalClients {...commonProps} />;
      case "Annual Surveillance Due":
        return <AnnualSurveillanceDueClient {...commonProps} />;
      case "Certificate Expiring":
        return <CertificateExpiring {...commonProps} />;
      default:
        return null;
    }
  };

  const handleDownload = () => {
    if (!filteredData || filteredData.length === 0) {
      alert("No data available to download");
      return;
    }
    exportToExcel(filteredData, `${activeTab}_Clients`);
  };

  const handleCertificateDownload = () => {
    if (!certificateFilteredData || certificateFilteredData.length === 0) {
      alert("No ISO 9001:2015 certificate data available to download");
      return;
    }

    // Create enhanced export data specifically for ISO 9001:2015
    const exportData = [];

    certificateFilteredData.forEach((client) => {
      if (client.servicesTaken && client.servicesTaken.length > 0) {
        // Filter only ISO 9001:2015 certificates
        const isoCertificates = client.servicesTaken.filter(
          (service) =>
            service.certificateId?.certificateName === "ISO 9001:2015" ||
            service.certificateId?.name === "ISO 9001:2015"
        );

        if (isoCertificates.length > 0) {
          isoCertificates.forEach((service) => {
            exportData.push({
              "Client Name": client.clientName || client.name || "",
              Phone: client.phone || client.phoneNumber || client.mobile || "",
              Email: client.email || "",
              Certificate:
                service.certificateId?.certificateName ||
                service.certificateId?.name ||
                "ISO 9001:2015",
              Status: client.status || "",
              "Expiry Date": service.expiryDate
                ? new Date(service.expiryDate).toLocaleDateString()
                : "",
              "Issue Date":
                service.issuedDate || service.issueDate
                  ? new Date(
                      service.issuedDate || service.issueDate
                    ).toLocaleDateString()
                  : "",
              "Certificate ID": service.certificateNumber || service._id || "",
            });
          });
        }
      }
    });

    if (exportData.length === 0) {
      alert("No clients found with ISO 9001:2015 certificate");
      return;
    }

    // Use XLSX for better control over export
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ISO 9001-2015 Clients");

    // Auto-size columns
    const colWidths = [
      { wch: 25 }, // Client Name
      { wch: 15 }, // Phone
      { wch: 30 }, // Email
      { wch: 20 }, // Certificate
      { wch: 12 }, // Status
      { wch: 15 }, // Expiry Date
      { wch: 15 }, // Issue Date
      { wch: 20 }, // Certificate ID
    ];
    ws["!cols"] = colWidths;

    // Generate filename with current date
    const today = new Date().toISOString().split("T")[0];
    const filename = `${activeTab}_Clients_ISO_9001_2015_${today}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="min-w-[320px] overflow-x-hidden">
      {/* Header Section */}
      <div className="py-6 md:py-12 px-4 sm:px-6">
        <div className="flex justify-between flex-col lg:flex-row gap-4 lg:gap-0">
          <h1 className="text-primaryColor text-xl sm:text-2xl md:text-3xl font-bold text-center lg:text-left">
            Our Clients
          </h1>

          <div className="flex flex-col sm:flex-row gap-2 flex-wrap justify-center lg:justify-end">
            {/* Mobile Search Bar */}
            <div className="searchBar md:hidden flex relative items-center w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search by Client id..."
                value={query}
                onChange={handleInputChange}
                className="pl-4 pr-10 py-2 border-2 border-primaryColor rounded-lg w-full"
              />
              <span className="absolute w-10 h-10 right-0 rounded-r-md bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] p-2">
                <FaSearch size={20} className="text-white" />
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {/* Certificate Download Button */}
              {appliedCertificateFilter && (
                <button
                  onClick={handleCertificateDownload}
                  className="flex items-center justify-center gap-2 px-3 py-2 h-11 border-2 rounded-lg bg-gradient-to-r from-[#28a745] to-[#155724] text-white hover:from-[#34ce57] hover:to-[#1e7e34] transition-colors shadow-md text-sm sm:text-base w-full sm:w-auto"
                >
                  <GrDownload size={16} />
                  <span>Download Data</span>
                </button>
              )}

              {/* Certificate Filter Button */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() =>
                    setShowCertificateFilter(!showCertificateFilter)
                  }
                  className={`flex items-center justify-center gap-2 px-3 py-2 h-11 border-2 rounded-lg transition-colors w-full sm:w-auto text-sm sm:text-base ${
                    appliedCertificateFilter
                      ? "bg-primaryColor text-white"
                      : "border-primaryColor text-primaryColor hover:bg-gray-50"
                  }`}
                >
                  <FaAward size={18} />
                  <span className="truncate">
                    {appliedCertificateFilter || "Filter by ISO 9001:2015"}
                  </span>
                </button>

                {/* Certificate Filter Dropdown */}
                {showCertificateFilter && (
                  <div className="absolute top-12 right-0 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[280px] sm:min-w-[300px] z-50 max-w-[90vw]">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filter by Certificate
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={
                                selectedCertificate === ISO_CERTIFICATE._id
                              }
                              onChange={(e) =>
                                setSelectedCertificate(
                                  e.target.checked ? ISO_CERTIFICATE._id : ""
                                )
                              }
                              className="mr-2"
                            />
                            ISO 9001:2015
                          </label>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-between pt-4 border-t">
                        <button
                          onClick={() => setShowCertificateFilter(false)}
                          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <div className="flex gap-2">
                          {appliedCertificateFilter && (
                            <button
                              onClick={clearCertificateFilter}
                              className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                            >
                              Clear
                            </button>
                          )}
                          <button
                            onClick={applyCertificateFilter}
                            disabled={!selectedCertificate}
                            className="px-3 py-2 text-sm bg-gradient-to-r from-[#28a745] to-[#155724] text-white rounded-md hover:from-[#34ce57] hover:to-[#1e7e34] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Date Filter Button */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 h-11 border-2 rounded-lg transition-colors w-full sm:w-auto text-sm sm:text-base ${
                    appliedFilter
                      ? "bg-gradient-to-r from-[#0070a8] to-[#023955] text-white border-[#0070a8]"
                      : "border-primaryColor text-primaryColor hover:bg-gray-50"
                  }`}
                >
                  <FaCalendarAlt size={14} />
                  <span className="truncate">
                    {appliedFilter || "Filter by Date"}
                  </span>
                </button>

                {/* Date Filter Dropdown */}
                {showDateFilter && (
                  <div className="absolute top-12 right-0 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[280px] sm:min-w-[300px] z-50 max-w-[90vw]">
                    <div className="space-y-4">
                      {/* Filter Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filter by Expiry Date
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="single"
                              checked={filterType === "single"}
                              onChange={(e) => {
                                setFilterType(e.target.value);
                                resetFilterForm();
                              }}
                              className="mr-2"
                            />
                            Single Month
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="range"
                              checked={filterType === "range"}
                              onChange={(e) => {
                                setFilterType(e.target.value);
                                resetFilterForm();
                              }}
                              className="mr-2"
                            />
                            Date Range
                          </label>
                        </div>
                      </div>

                      {/* Single Month Selection */}
                      {filterType === "single" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Month
                            </label>
                            <select
                              value={singleMonth}
                              onChange={(e) => setSingleMonth(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            >
                              <option value="">Month</option>
                              {months.map((month) => (
                                <option key={month.value} value={month.value}>
                                  {month.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Year
                            </label>
                            <select
                              value={singleYear}
                              onChange={(e) => setSingleYear(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            >
                              <option value="">Year</option>
                              {years.map((year) => (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Date Range Selection */}
                      {filterType === "range" && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              From
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <select
                                value={startMonth}
                                onChange={(e) => setStartMonth(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">Month</option>
                                {months.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={startYear}
                                onChange={(e) => setStartYear(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">Year</option>
                                {years.map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              To
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <select
                                value={endMonth}
                                onChange={(e) => setEndMonth(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">Month</option>
                                {months.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={endYear}
                                onChange={(e) => setEndYear(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">Year</option>
                                {years.map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row justify-between pt-4 border-t gap-3 sm:gap-0">
                        <button
                          onClick={() => setShowDateFilter(false)}
                          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 order-2 sm:order-1"
                        >
                          Cancel
                        </button>
                        <div className="flex gap-2 order-1 sm:order-2">
                          {appliedFilter && (
                            <button
                              onClick={clearDateFilter}
                              className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                            >
                              Clear
                            </button>
                          )}
                          <button
                            onClick={applyDateFilter}
                            disabled={
                              (filterType === "single" &&
                                (!singleMonth || !singleYear)) ||
                              (filterType === "range" &&
                                (!startMonth ||
                                  !startYear ||
                                  !endMonth ||
                                  !endYear))
                            }
                            className="px-3 py-2 text-sm bg-gradient-to-r from-[#0070a8] to-[#023955] text-white rounded-md hover:from-[#1e8cbf] hover:to-[#1674a3] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Search Bar */}
              <div className="searchBar hidden md:flex relative items-center lg:-mt-7 xl:mt-0">
                <input
                  type="text"
                  placeholder="Search by Client id..."
                  value={query}
                  onChange={handleInputChange}
                  className="pl-4 pr-10 py-2 border-2 border-primaryColor rounded-lg w-full sm:w-auto"
                />
                <span className="absolute w-10 h-10 right-0 rounded-r-md bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] p-2">
                  <FaSearch size={20} className="text-white" />
                </span>
              </div>

              {/* Add button */}
              <button
                onClick={openModal}
                className="flex items-center justify-center pl-3 pr-3 py-2 h-11 border-2 bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white rounded-lg text-sm sm:text-base w-full sm:w-auto md:flex"
              >
                + Add New
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {(appliedFilter || appliedCertificateFilter) && (
        <div className="px-4 sm:px-6 pb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <span className="whitespace-nowrap">Active filters:</span>
            {appliedFilter && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                Expiry Date: {appliedFilter}
                <button
                  onClick={clearDateFilter}
                  className="hover:text-blue-900"
                >
                  <FaTimes size={12} />
                </button>
              </span>
            )}
            {appliedCertificateFilter && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                Certificate: {appliedCertificateFilter}
                <button
                  onClick={clearCertificateFilter}
                  className="hover:text-green-900"
                >
                  <FaTimes size={12} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 sm:px-6">
        <div className="border-b tracking-wide overflow-x-auto">
          <div className="flex justify-center min-w-max">
            {[
              "Active",
              "Suspended",
              "Withdrawal",
              "Annual Surveillance Due",
              "Certificate Expiring",
            ].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-3 sm:px-4 md:px-6 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? "border-primaryColor text-primaryColor bg-purple-50"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto mt-4 px-2 sm:px-4">
        <div className="min-w-[300px]">{renderTabContent()}</div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-center items-center my-6 px-4 gap-4 sm:gap-6">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
              color="primary"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  minWidth: { xs: "32px", sm: "40px" },
                  height: { xs: "32px", sm: "40px" },
                },
              }}
            />
          </Stack>
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            Showing {Math.min((page - 1) * itemsPerPage + 1, totalItems)} to{" "}
            {Math.min(page * itemsPerPage, totalItems)} of {totalItems} results
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isModalOpen && (
        <AddClientModal
          onClose={closeModal}
          onSubmit={() => setIsModalOpen(false)}
          mode="add"
        />
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <AddClientModal
          onClose={closeModal}
          mode="edit"
          data={selectedClient}
        />
      )}
    </div>
  );
};

export default OurClients;
